import { computed, watch } from 'vue'
import { state } from './state'
// Installs the reactive clock lib/dates.js reads today() from.
import './clock'
import { loadSettings } from './settings'
import { loadPlans } from './plans'
import { loadBudgets } from './budgets'
import { intlLocale, t } from './i18n'
import { loadGoals } from './goals'
import { loadTags } from './tags'
import { loadCategories } from './categories'
import { ensureStartedOn, loadAchievements } from './achievements'
import { currentTheme } from './theme'
import { fromDb, tracked } from './live'
import { query } from '../db'
import {
  balanceSeries,
  buildAlerts,
  computeAllowance,
  goalProgress,
  incomeCheck,
  dailySeries,
  monthEnds,
  monthlySeries,
  plannedBetween,
} from '../lib/analytics'
import { autoPeriodsOf as planAutoPeriods, buildSchedule, isAuto, withAutoAmounts } from '../lib/matching'
import { alarming, autoPeriodsOf as budgetAutoPeriods, budgetTone, buildBudgets, periodOf } from '../lib/budgets'
import { savingsEntries, setAsideOn } from '../lib/savings'
import { stepBy } from '../lib/recurrence'
import {
  HAYSTACK,
  LEDGER,
  ROW_COLUMNS,
  and,
  clause,
  containing,
  or,
  placeholders,
  readRow,
  savingsParam,
  where,
} from '../lib/ledger-sql'
import {
  addDays,
  endOfMonth,
  fullDay,
  monthOf,
  shiftMonth,
  startOfMonth,
  thisMonth,
  today as todayISO,
} from '../lib/dates'
import { formatMoney, parseAmount, round2 } from '../lib/money'
import { parseDateCell } from '../lib/statement'
import { compareMonths } from '../lib/compare'
import { count } from './usage'

export { state } from './state'

/**
 * Loads what the app keeps whole — settings, plans, budgets, goals, tags and
 * categories, all small. The transactions are not among them: every question
 * about them is asked of the database (below), and the list reads its rows a
 * page at a time.
 */
export async function hydrate() {
  await loadSettings()
  state.settings.theme = currentTheme()
  await Promise.all([loadPlans(), loadBudgets(), loadGoals(), loadTags(), loadCategories(), loadAchievements()])
  await ensureStartedOn()
  state.ready = true
}

/* ------------------------------------------------------------------ */
/* Asking the database. Every figure about the transactions below is a */
/* query over `LEDGER` (lib/ledger-sql.js): the real rows and the      */
/* goals' projected savings, one shape. `fromDb` keeps each answer.    */
/* ------------------------------------------------------------------ */

/** Every goal written out as rows up to today — they live in no table. */
const allSavings = computed(() => state.goals.flatMap((goal) => savingsEntries(goal, todayISO())))

/** The same, as the one parameter every ledger question starts with. */
const savingsJson = computed(() => savingsParam(allSavings.value))

/** A question about the ledger: `LEDGER` first, then the statement. */
const ask = (savings, sql, params = []) => query(`${LEDGER} ${sql}`, [savings, ...params])

/** Facts about the whole ledger that several things need: its size, where it starts, and its sum to today. */
const ledgerFacts = fromDb(
  () => ({ today: todayISO() }),
  async ({ today }) => {
    const [r] = await query(
      `SELECT count(*) AS count, min(date) AS first,
              total(CASE WHEN date <= ? THEN amount END) AS toToday
         FROM transactions`,
      [today],
    )
    return { count: r.count, first: r.first ?? null, toToday: r.toToday }
  },
  { count: 0, first: null, toToday: 0 },
)

/** How many transactions there are at all — the duplicates button, a first import. */
export const transactionCount = computed(() => ledgerFacts.value.count)

/** The first day the ledger holds, or null — where an automatic budget's memory starts. */
export const ledgerStart = computed(() => ledgerFacts.value.first)

/* ------------------------------------------------------------------ */
/* Derived views. Components read these; nothing writes to them.       */
/* ------------------------------------------------------------------ */

export const today = computed(() => todayISO())
export const currency = computed(() => state.settings.currency)

const MASK = '••••'

/** Currency-aware formatter, reactive to the settings. Strict privacy mode
 *  hides every amount, wherever it is drawn. */
export function fmt(amount, opts = {}) {
  if (state.settings.privacy === 'strict') return MASK
  return formatMoney(amount, {
    currency: state.settings.currency,
    locale: intlLocale.value,
    ...opts,
  })
}

/** The same, for figures that describe the whole account rather than one row —
 *  balance, allowance, month totals, savings. These are what privacy mode
 *  hides first, so use it for anything that would give the game away across
 *  the room. */
export function fmtGlobal(amount, opts = {}) {
  return state.settings.privacy === 'off' ? fmt(amount, opts) : MASK
}

/** Privacy mode, for the figures that are not money and so cannot go through
 *  the formatters above — a progress percentage, say. */
export const hideGlobal = computed(() => state.settings.privacy !== 'off')
export const hideAmounts = computed(() => state.settings.privacy === 'strict')

/**
 * The month the plan, the month totals and the alerts are about. There is no
 * month switch any more: the range above the search is the only way of moving
 * about the calendar, so the month follows it — the one it ends in, or the real
 * one when the range is open at both ends.
 */
export const viewedMonth = computed(() => {
  const { from, to } = state.ui
  if (to) return monthOf(to)
  if (from) return monthOf(from)
  return thisMonth()
})

export const isCurrentMonth = computed(() => viewedMonth.value === thisMonth())

/** What the bank would show — savings are a promise, not a withdrawal. */
export const accountBalance = computed(() =>
  round2((Number(state.settings.openingBalance) || 0) + ledgerFacts.value.toToday),
)

/** What you may spend: the account, less whatever the goals have set aside. */
export const balance = computed(() => round2(accountBalance.value - setAsideOn(state.goals, todayISO())))

/** Money in and out over the month being viewed. */
export const totals = fromDb(
  () => ({ from: startOfMonth(viewedMonth.value), to: endOfMonth(viewedMonth.value) }),
  async ({ from, to }) => {
    const [r] = await query(
      `SELECT total(CASE WHEN amount > 0 THEN amount END) AS income,
              total(CASE WHEN amount < 0 THEN -amount END) AS spent,
              count(*) AS count
         FROM transactions WHERE date BETWEEN ? AND ?`,
      [from, to],
    )
    return { income: round2(r.income), spent: round2(r.spent), net: round2(r.income - r.spent), count: r.count }
  },
  { income: 0, spent: 0, net: 0, count: 0 },
)

/**
 * The viewed month against the one before: money in, out and set aside, and
 * the biggest category of each (`lib/compare.js`). Whole months both, and never
 * narrowed by the filters — like the month totals, it is about the account.
 * A saving is a goal's row: an instalment, or the opening amount put by on the
 * day it began — that is when the balance lets go of it, so it is that month's.
 */
export const monthComparison = fromDb(
  () => ({ savings: savingsJson.value, month: viewedMonth.value }),
  async ({ savings, month }) => {
    const previous = shiftMonth(month, -1)
    const groups = await ask(
      savings,
      `SELECT substr(l.date, 1, 7) AS month,
              CASE WHEN l.saving THEN 'saved' WHEN l.amount > 0 THEN 'income' ELSE 'spent' END AS kind,
              l.category AS key,
              total(abs(l.amount)) AS sum
         FROM ledger l
        WHERE l.date BETWEEN ? AND ? AND l.amount <> 0
        GROUP BY 1, 2, 3`,
      [startOfMonth(previous), endOfMonth(month)],
    )
    return { month, previous, ...compareMonths(groups, month, previous) }
  },
  null,
)

/**
 * The transactions the plans are matched against — never the whole ledger.
 * Each plan wants only rows in its own category, and only around the months on
 * screen: from as many occurrences back as an automatic plan averages (plus
 * one, for the nearest-slot split at the edge) to one occurrence past the end.
 * The viewed month and the real one are both covered, since the allowance
 * always reads the real one. A one-off plan looks a month either side of its day.
 */
const planRows = fromDb(
  () => {
    const plans = state.plans.filter((plan) => !plan.archived && plan.category_id)
    if (!plans.length) return { parts: [] }
    const lo = [startOfMonth(viewedMonth.value), startOfMonth(thisMonth())].sort()[0]
    const hi = [endOfMonth(viewedMonth.value), endOfMonth(thisMonth())].sort()[1]
    return {
      parts: plans.map((plan) =>
        plan.cadence === 'once'
          ? [plan.category_id, addDays(plan.start_date, -31), addDays(plan.start_date, 31)]
          : [
              plan.category_id,
              stepBy(plan.cadence, lo, -((isAuto(plan) ? planAutoPeriods(plan) : 0) + 1)),
              stepBy(plan.cadence, hi, 1),
            ],
      ),
    }
  },
  async ({ parts }) => {
    if (!parts.length) return []
    const each = parts.map(() => '(t.category_id = ? AND t.date BETWEEN ? AND ?)').join(' OR ')
    return query(
      `SELECT t.id, t.date, t.amount, t.description, t.created_at, COALESCE(c.name, '') AS category
         FROM transactions t LEFT JOIN categories c ON c.id = t.category_id
        WHERE ${each}`,
      parts.flat(),
    )
  },
  [],
)

/** Plan-versus-reality rows for the month being viewed. */
export const schedule = computed(() =>
  buildSchedule(state.plans, planRows.value, {
    from: startOfMonth(viewedMonth.value),
    to: endOfMonth(viewedMonth.value),
    today: todayISO(),
  }),
)

/** The same, pinned to the real current month — the allowance never moves
 *  just because you are browsing history. */
const liveSchedule = computed(() =>
  viewedMonth.value === thisMonth()
    ? schedule.value
    : buildSchedule(state.plans, planRows.value, {
        from: startOfMonth(thisMonth()),
        to: endOfMonth(thisMonth()),
        today: todayISO(),
      }),
)

export const plannedIncome = computed(() => schedule.value.filter((r) => r.type === 'income'))
export const plannedExpenses = computed(() => schedule.value.filter((r) => r.type === 'expense'))

/**
 * The budgets over the month being viewed. A budget's period is its own — a
 * weekly one lands four or five times in a month — so this is one row per
 * period the month touches, not one row per budget.
 */
export const budgetRows = computed(() =>
  buildBudgets(resolvedBudgets.value, budgetTransactions.value, {
    from: startOfMonth(viewedMonth.value),
    to: endOfMonth(viewedMonth.value),
    today: todayISO(),
    // Where the ledger begins: an automatic budget's memory does too.
    since: ledgerFacts.value.first,
    // Inherited tags count: a budget on #travel covers everything filed under
    // a category that wears it.
    tagsOf: tagIdsOn,
  }).map((row) => ({ ...row, name: budgetName(row) })),
)

/**
 * The transactions the budgets count, asked for budget by budget: in any of its
 * categories or wearing any of its tags (a category's tags included), from as
 * many periods back as an automatic one averages to a period past the month.
 */
const budgetTransactions = fromDb(
  () => {
    const from = startOfMonth(viewedMonth.value)
    const to = endOfMonth(viewedMonth.value)
    const parts = []
    for (const budget of state.budgets) {
      if (budget.archived || !budget.start_date) continue
      const categoryIds = budget.categoryIds ?? []
      const tagIds = budget.tagIds ?? []
      if (!categoryIds.length && !tagIds.length) continue
      const first = periodOf(budget, from)?.from ?? from
      const back = budget.auto ? budgetAutoPeriods(budget) : 0
      parts.push({ categoryIds, tagIds, lo: stepBy(budget.cadence, first, -back), hi: stepBy(budget.cadence, to, 1) })
    }
    return { parts }
  },
  async ({ parts }) => {
    if (!parts.length) return []
    const conditions = parts.map((part) => {
      const covered = or([
        part.categoryIds.length && clause(`t.category_id IN (${placeholders(part.categoryIds)})`, part.categoryIds),
        part.tagIds.length &&
          clause(
            `(EXISTS (SELECT 1 FROM transaction_tags x WHERE x.transaction_id = t.id AND x.tag_id IN (${placeholders(part.tagIds)}))
              OR EXISTS (SELECT 1 FROM category_tags x WHERE x.category_id = t.category_id AND x.tag_id IN (${placeholders(part.tagIds)})))`,
            [...part.tagIds, ...part.tagIds],
          ),
      ])
      return and([clause('t.date BETWEEN ? AND ?', [part.lo, part.hi]), covered])
    })
    const w = where(or(conditions))
    const rows = await query(
      `SELECT t.id, t.date, t.amount, t.description, t.created_at, COALESCE(c.name, '') AS category,
              (SELECT group_concat(tag_id) FROM transaction_tags WHERE transaction_id = t.id) AS own_tags
         FROM transactions t LEFT JOIN categories c ON c.id = t.category_id ${w.sql}`,
      w.params,
    )
    return rows.map(readRow)
  },
  [],
)

/**
 * Budgets with what they cover turned from ids into what `lib/budgets.js`
 * matches on: category *names* (a transaction stores the name) and tag ids.
 * Resolved here rather than at load so a rename shows at once, and anything
 * deleted since simply drops out.
 */
const resolvedBudgets = computed(() => {
  const categoryById = new Map(state.categories.map((c) => [c.id, c.name]))
  return state.budgets.map((budget) => ({
    ...budget,
    categories: (budget.categoryIds ?? []).map((id) => categoryById.get(id)).filter(Boolean),
    tags: (budget.tagIds ?? []).filter((id) => tagById.value.has(id)),
  }))
})

/**
 * What a budget is called: it has no name of its own, so it is what it covers —
 * its categories, then its tags.
 */
export const budgetName = (budget) =>
  [
    ...(budget?.categories ?? []),
    ...(budget?.tags ?? []).map((id) => tagById.value.get(id)?.name).filter(Boolean),
  ].join(', ')

/**
 * Looking into a budget: the ledger, filtered to what it covers.
 *
 * The filters combine families with *and*, and a budget combines them with
 * *or*, so one that covers both categories and tags cannot be shown exactly.
 * Its categories are what it is filtered by then — the tags are usually the
 * extra, not the core.
 */
export function focusBudget(budget) {
  const categories = budget?.categories ?? []
  const tags = budget?.tags ?? []
  setFilter('categories', categories)
  setFilter('tags', categories.length ? [] : tags)
  state.ui.panel = 'transactions'
}

/**
 * The budgets in one line: how many, how they are doing, and what the spending
 * limits among them come to.
 *
 * Only limits are added up. An income target is money coming the other way, and
 * a single figure mixing the two would be a number that means nothing — but the
 * counts cover every budget, because either kind can be off track.
 */
export const budgetHealth = computed(() => {
  const rows = budgetRows.value
  const limits = rows.filter((r) => r.type === 'expense')
  const budgeted = round2(limits.reduce((sum, r) => sum + r.expected, 0))
  const spent = round2(limits.reduce((sum, r) => sum + r.actual, 0))
  const tones = rows.map(budgetTone)
  // The card's own colour comes from the total, judged by exactly the rule a
  // single budget is judged by: the paddings add up the way the figures do.
  // Otherwise a card reading "200 under" could be red because one row breached.
  const room = limits.reduce((sum, r) => sum + r.expected * r.tolerance, 0)
  const variance = round2(spent - budgeted)
  return {
    total: rows.length,
    limits: limits.length,
    alarming: rows.filter(alarming).length,
    running: rows.filter((r) => r.status === 'running').length,
    budgeted,
    spent,
    // The headline question a budget answers: how far the wrong side of the
    // figure are you, or how much is still yours to spend.
    variance,
    left: round2(Math.max(0, budgeted - spent)),
    progress: budgeted > 0 ? Math.min(1, spent / budgeted) : 0,
    tone: variance > room ? 'danger' : variance > 0 ? 'warn' : 'good',
    // The worst any single one is doing, which is what the off-track count is
    // about — a budget can be red while the others leave the total under.
    worst: tones.includes('danger') ? 'danger' : tones.includes('warn') ? 'warn' : 'good',
  }
})

export const goalStates = computed(() =>
  state.goals
    .filter((g) => !g.archived)
    .map((g) => goalProgress(g, todayISO())),
)

/** This month's transactions — what the allowance measures today's spending by. */
const thisMonthRows = fromDb(
  () => ({ from: startOfMonth(thisMonth()), to: endOfMonth(thisMonth()) }),
  ({ from, to }) => query('SELECT id, date, amount FROM transactions WHERE date BETWEEN ? AND ?', [from, to]),
  [],
)

export const allowance = computed(() =>
  computeAllowance({
    balance: balance.value,
    schedule: liveSchedule.value,
    goalStates: goalStates.value,
    transactions: thisMonthRows.value,
    settings: state.settings,
    today: todayISO(),
  }),
)

export const income = computed(() => incomeCheck(schedule.value, viewedMonth.value, todayISO()))

export const alerts = computed(() =>
  buildAlerts({
    schedule: schedule.value,
    budgets: budgetRows.value,
    goalStates: goalStates.value,
    allowance: allowance.value,
    income: income.value,
    month: viewedMonth.value,
    fmt: (n) => fmtGlobal(n),
    t,
    today: todayISO(),
  }),
)

/* ------------------------------------------------------------------ */
/* Chart series. The range above the search decides the window: the two */
/* dates it holds, or every month the rows touch when it holds neither. */
/* ------------------------------------------------------------------ */

/**
 * True when the ledger is narrowed to a subset — a search, a kind, or a focused
 * plan or goal. A window is not a subset: the two date bounds move where the
 * charts look, not which of your money counts, so the balance line stays a
 * balance when you pick a range.
 */
export const chartsFiltered = computed(
  () =>
    !!state.ui.search.trim() ||
    FILTERS.some((f) => !RANGE_KEYS.includes(f.key) && (isSingle(f) ? state.ui[f.key] != null : state.ui[f.key].length > 0)),
)

/**
 * The balance line counts from zero whenever the chart is about a subset —
 * a filtered ledger, or a tag being compared against the whole. Tag lines start
 * at zero by nature, and lines that share an origin can be read against each
 * other.
 */
export const balanceFromZero = computed(
  () => chartsFiltered.value || state.ui.chartSeries.some(Boolean),
)

/**
 * What the charts are narrowed by: exactly what the list is — when anything
 * narrows it. Otherwise the whole ledger, savings and all.
 */
const chartCondition = computed(() => (chartsFiltered.value ? viewCondition.value : null))

/**
 * The first and last day the charted rows touch — real money only, as the list
 * shows it — for a window with an open end.
 */
const chartBounds = fromDb(
  () => ({ savings: savingsJson.value, condition: chartCondition.value }),
  async ({ savings, condition }) => {
    const w = where(and([clause('l.saving = 0'), condition]))
    const [r] = await ask(savings, `SELECT min(l.date) AS lo, max(l.date) AS hi FROM ledger l ${w.sql}`, w.params)
    return r?.lo ? [r.lo, r.hi] : null
  },
  null,
)

/**
 * The stretch of calendar itself, worked out without reference to the grain —
 * what a bucket is depends on how long the window turns out to be, so asking
 * the other way round would be a circle.
 */
const chartSpan = computed(() => {
  const [lo, hi] = [state.ui.from, state.ui.to]
  const span = chartBounds.value ?? [todayISO(), todayISO()]
  return {
    from: lo ?? startOfMonth(monthOf(span[0])),
    to: hi ?? endOfMonth(monthOf(span[1])),
  }
})

/**
 * What a bucket is on the charts. A window inside one month has only that month
 * in it, so there is nothing to group by there — it is days or nothing.
 */
export const canChartByMonth = computed(
  () => monthOf(chartSpan.value.from) !== monthOf(chartSpan.value.to),
)
export const chartGrain = computed(() =>
  canChartByMonth.value ? state.ui.chartGrain : 'day',
)
export const setChartGrain = (grain) => {
  if (grain === 'month' && !canChartByMonth.value) return false
  state.ui.chartGrain = grain
  return true
}

/**
 * The stretch of calendar every list and chart is looking through: the range,
 * or — when it holds nothing — every whole month the rows touch. One window,
 * named in one place, rather than a switch and a range disagreeing about which
 * of them is in charge.
 */
export const chartWindow = computed(() => ({ grain: chartGrain.value, ...chartSpan.value }))

/** The window as the picker says it: both bounds, or neither. */
export const chartRange = computed(() => ({
  from: state.ui.from,
  to: state.ui.to,
  set: state.ui.from != null || state.ui.to != null,
}))

/**
 * Moves both bounds at once. A pair that arrives backwards is read as the span
 * the user meant rather than as nothing at all.
 */
export function setDateRange(from, to) {
  const [lo, hi] = from && to && from > to ? [to, from] : [from, to]
  setDateFilter('from', lo || null)
  setDateFilter('to', hi || null)
}

/** Back to everything, leaving every other filter where it is. */
export const clearDateRange = () => setDateRange(null, null)

/** The window set to one whole month — what the old month switch used to do. */
export const showMonth = (month) => setDateRange(startOfMonth(month), endOfMonth(month))

const monthsIn = (from, to) => {
  const months = []
  for (let m = monthOf(from); m <= monthOf(to); m = shiftMonth(m, 1)) months.push(m)
  return months
}

/** Before the window: one bucket, which carries the balance in. */
const BEFORE = '0000-00-00'

/**
 * Money in, out and put by per day up to `to`, as the database adds it up —
 * with everything before `from` in one bucket dated `BEFORE`. Returned as rows
 * the series functions in lib/analytics.js read like transactions (one per
 * direction per day), so the charts keep one way of drawing whatever they
 * are handed.
 */
async function dailyFlows(savings, condition, from, to) {
  const w = where(and([condition, clause('l.date <= ?', [to])]))
  const rows = await ask(
    savings,
    `SELECT CASE WHEN l.date < ? THEN '${BEFORE}' ELSE l.date END AS day,
            total(CASE WHEN l.saving = 0 AND l.amount > 0 THEN l.amount END) AS income,
            total(CASE WHEN l.saving = 0 AND l.amount < 0 THEN l.amount END) AS spent,
            total(CASE WHEN l.saving = 1 THEN l.amount END) AS saved
       FROM ledger l ${w.sql}
      GROUP BY day`,
    [from, ...w.params],
  )
  const out = []
  for (const r of rows) {
    if (r.income) out.push({ date: r.day, amount: r.income })
    if (r.spent) out.push({ date: r.day, amount: r.spent })
    if (r.saved) out.push({ date: r.day, amount: r.saved, saving: true })
  }
  return out
}

/** Everything the charts draw beside the chips: the whole (or the filtered) ledger's flows. */
const chartFlows = fromDb(
  () => ({ savings: savingsJson.value, condition: chartCondition.value, from: chartSpan.value.from, to: chartSpan.value.to }),
  ({ savings, condition, from, to }) => dailyFlows(savings, condition, from, to),
  [],
)

/**
 * The balance line. It stops at today — a balance is never projected — and when
 * the ledger is filtered it becomes a running total of the rows on screen,
 * counted from zero: a subset of your money is not your balance.
 */
export const balanceTrend = computed(() => {
  const { grain, from, to } = chartWindow.value
  const end = [to, todayISO()].sort()[0]
  if (end < from) return []
  const opening = balanceFromZero.value ? 0 : state.settings.openingBalance
  // The savings are rows like any other here, so the line dips on the day the
  // ledger shows one — and the two cannot disagree about when that was.
  const daily = balanceSeries(chartFlows.value, opening, from, end)
  return grain === 'month' ? monthEnds(daily) : daily
})

/** Money in and out, per month over all time or per day within a month. */
export const trendBars = computed(() => {
  const { grain, from, to } = chartWindow.value
  return grain === 'month'
    ? monthlySeries(chartFlows.value, monthsIn(from, to))
    : dailySeries(chartFlows.value, from, to)
})

/**
 * The lines drawn beside the everything series: a tag's own running total for
 * the balance chart, and how much it moved per bucket for the other. Tags and
 * categories share the slots, and so share the colours — there are only so many
 * hues a reader can tell apart, and two families quietly reusing them would be
 * a lie about which line is which.
 */
export const MAX_CHART_SERIES = 8

/** What each kind of line counts as its own, as a condition on `ledger l`. */
const BELONGS = {
  tag: (id) => clause('EXISTS (SELECT 1 FROM worn w WHERE w.tx = l.id AND w.tag = ?)', [id]),
  category: (id) => clause('l.category = ?', [id]),
}

const nameOf = (kind, id) => (kind === 'tag' ? tagById.value.get(id)?.name : id)

/**
 * What the charts are about. With "All" on that is the whole ledger, whatever
 * else is drawn beside it; with "All" off the chips are the subject, so a chart
 * that can only show one thing — the breakdown — shows theirs.
 */
export const chartSubject = computed(() => {
  const picks = state.ui.chartSeries
    .map((entry, slot) => (entry ? { ...entry, slot, name: nameOf(entry.kind, entry.id) } : null))
    .filter((pick) => pick?.name)
  const all = state.ui.chartAll || !picks.length
  return {
    all,
    picks,
    names: picks.map((pick) => pick.name),
    // The savings too: each goal is a category, so what was put aside is a
    // slice of where the money went, named after the goal.
    condition: and([chartCondition.value, all ? null : or(picks.map((pick) => BELONGS[pick.kind](pick.id)))]),
  }
})

/** Each chip's own flows: the charted rows that are its tag's or its category's. */
const lineFlows = fromDb(
  () => ({
    savings: savingsJson.value,
    condition: chartCondition.value,
    picks: chartSubject.value.picks.map(({ kind, id, slot }) => ({ kind, id, slot })),
    from: chartSpan.value.from,
    to: chartSpan.value.to,
  }),
  async ({ savings, condition, picks, from, to }) => {
    const out = {}
    // From the ledger *with* the savings in it, so a goal's virtual category
    // has rows to draw; a real category's line is untouched, since no saving
    // is filed under one.
    for (const pick of picks) {
      out[pick.slot] = await dailyFlows(savings, and([condition, BELONGS[pick.kind](pick.id)]), from, to)
    }
    return out
  },
  {},
)

export const chartLineSeries = computed(() => {
  const { grain, from, to } = chartWindow.value
  const end = [to, todayISO()].sort()[0]
  const out = []
  for (const pick of chartSubject.value.picks) {
    const rows = lineFlows.value[pick.slot] ?? []
    const flow =
      grain === 'month'
        ? monthlySeries(rows, monthsIn(from, to))
        : dailySeries(rows, from, to)
    out.push({
      kind: pick.kind,
      id: pick.id,
      name: pick.name,
      slot: pick.slot,
      balance:
        end < from
          ? []
          : (grain === 'month'
              ? monthEnds(balanceSeries(rows, 0, from, end))
              : balanceSeries(rows, 0, from, end)
            ).map((p) => p.value),
      // Savings bucket as `saved`, not `spent` — counted here all the same.
      moved: flow.map((r) => round2(r.income + r.spent + r.saved)),
    })
  }
  return out
})

/**
 * Where the money goes, split by category — the same split whichever way it is
 * drawn, so a slice and its band are always the same colour.
 *
 * Spending only: a pie of mixed directions would be adding money that came in
 * to money that went out and calling the sum a whole. Rows with no category are
 * their own slice rather than being dropped, because "I do not know" is an
 * answer worth seeing the size of.
 */
// Keys, not words: they identify the two catch-all slices (and keep their
// colours) whatever the language. What is *shown* is `labelOf(key)`.
export const UNCATEGORISED = 'Uncategorised'
const OTHER = 'Other'

/** A slice's name as the reader sees it: the two catch-alls in their language. */
const labelOf = (key) =>
  key === UNCATEGORISED ? t('charts.uncategorised') : key === OTHER ? t('charts.other') : key

/** Eight slices at most: past that a reader is matching colours, not reading. */
export const MAX_SLICES = 8
const NAMED_SLICES = MAX_SLICES - 2 // the two neutrals below are kept back

/**
 * The breakdowns' raw material, added up by the database over the window:
 * per category (in, out, and whether it is a goal's), and — when chips are
 * chosen — per chip. A row counts for the first chip it answers to, in slot
 * order, so a transaction both tagged and categorised is not counted twice and
 * the slices still add up to the whole; what answers to none is slot -1.
 */
const chartGroups = fromDb(
  () => {
    const { all, picks, condition } = chartSubject.value
    return {
      savings: savingsJson.value,
      condition,
      all,
      picks: picks.map(({ kind, id, slot }) => ({ kind, id, slot })),
      from: chartSpan.value.from,
      to: chartSpan.value.to,
    }
  },
  async ({ savings, condition, picks, from, to }) => {
    const w = where(and([condition, clause('l.date BETWEEN ? AND ?', [from, to])]))
    const byCategory = await ask(
      savings,
      `SELECT l.category AS key,
              total(CASE WHEN l.amount > 0 THEN l.amount END) AS came_in,
              total(CASE WHEN l.amount < 0 THEN -l.amount END) AS went_out,
              max(l.saving) AS goal
         FROM ledger l ${w.sql}
        GROUP BY l.category`,
      w.params,
    )
    let byPick = []
    if (picks.length) {
      const cases = picks.map((pick) => BELONGS[pick.kind](pick.id))
      byPick = await ask(
        savings,
        `SELECT CASE ${cases.map((c, i) => `WHEN ${c.sql} THEN ${i}`).join(' ')} ELSE -1 END AS pick,
                total(CASE WHEN l.amount > 0 THEN l.amount END) AS came_in,
                total(CASE WHEN l.amount < 0 THEN -l.amount END) AS went_out
           FROM ledger l ${w.sql}
          GROUP BY pick`,
        [...cases.flatMap((c) => c.params), ...w.params],
      )
    }
    return { byCategory, byPick }
  },
  { byCategory: [], byPick: [] },
)

/**
 * The chips against everything else: one slice each, in slot order and wearing
 * the colour of their own line, and the rest in one bag.
 */
function pickedSplit(picks, groups, value) {
  const totals = new Map(groups.map((g) => [g.pick, value(g)]))
  const slices = picks
    .map((pick, i) => ({
      name: pick.name,
      key: `${pick.kind}:${pick.id}`,
      value: round2(totals.get(i) ?? 0),
      color: `--c-series-${pick.slot + 1}`,
    }))
    .filter((slice) => slice.value > 0)
  const other = round2(totals.get(-1) ?? 0)
  if (other > 0) slices.push({ name: labelOf(OTHER), key: OTHER, value: other, color: '--c-muted' })
  return slices
}

const splitBy = (direction) =>
  computed(() => {
    const subject = chartSubject.value
    const { byCategory, byPick } = chartGroups.value
    // Chips chosen *beside* the everything series: each is a slice of the whole,
    // and what none of them claims is the rest of it.
    if (subject.all && subject.picks.length) {
      return withShares(pickedSplit(subject.picks, byPick, (g) => g[direction]))
    }
    return withShares(namedSplit(new Map(byCategory.map((g) => [g.key || UNCATEGORISED, g[direction]]))))
  })

/** The two halves of the ledger, each split the same way. */
export const spendingSplit = splitBy('went_out')
export const incomeSplit = splitBy('came_in')

/**
 * The treemap: every category at once, each the size of all the money it
 * moved — in *and* out, which the pies never add together. The colour says
 * which way: green for a category money came in through, amber for one it
 * went out through, purple for a goal's savings, a neutral for the rows filed
 * nowhere. A category that saw both ways takes the colour of the larger.
 *
 * The chips work on it as they do on the pies: beside "All", each chip is a
 * tile in its line's colour and everything else is one "Other"; with "All" off
 * the chips are the whole of it, as if they were all the money there was.
 */
export const categoryTreemap = computed(() => {
  const subject = chartSubject.value
  const { byCategory, byPick } = chartGroups.value

  if (subject.picks.length) {
    // With "All" off the rows are only the chips', so Other comes out empty.
    return withShares(pickedSplit(subject.picks, byPick, (g) => g.came_in + g.went_out))
  }

  return withShares(
    byCategory
      .map((g) => {
        const key = g.key || UNCATEGORISED
        const goal = !!g.goal
        return {
          name: labelOf(key),
          key,
          value: round2(g.came_in + g.went_out),
          direction: goal ? 'saved' : g.came_in > g.went_out ? 'in' : 'out',
          color:
            key === UNCATEGORISED
              ? '--c-faint'
              : goal
                ? '--c-goal'
                : g.came_in > g.went_out
                  ? '--c-accent'
                  : '--c-warn',
        }
      })
      .filter((tile) => tile.value > 0)
      .sort((a, b) => b.value - a.value),
  )
})

function namedSplit(spent) {
  const loose = round2(spent.get(UNCATEGORISED) ?? 0)
  spent.delete(UNCATEGORISED)
  const named = [...spent.entries()]
    .map(([name, value]) => ({ name, value: round2(value) }))
    .filter((slice) => slice.value > 0)
    .sort((a, b) => b.value - a.value)

  // The hues are the categories themselves; the two bags of leftovers wear
  // neutrals, since neither of them is a category you chose.
  const slices = named.slice(0, NAMED_SLICES).map((slice, i) => ({
    ...slice,
    key: slice.name,
    color: `--c-series-${i + 1}`,
  }))
  const rest = round2(named.slice(NAMED_SLICES).reduce((sum, slice) => sum + slice.value, 0))
  if (rest > 0) slices.push({ name: labelOf(OTHER), key: OTHER, value: rest, color: '--c-muted' })
  if (loose > 0) {
    slices.push({ name: labelOf(UNCATEGORISED), key: UNCATEGORISED, value: loose, color: '--c-faint' })
  }
  return slices
}

/** What each slice is worth against the whole. */
function withShares(slices) {
  const total = round2(slices.reduce((sum, slice) => sum + slice.value, 0))
  return slices.map((slice) => ({ ...slice, share: total > 0 ? slice.value / total : 0 }))
}

/** Which slot a line holds, or -1 when it is not on the charts. */
export const chartSlotOf = (kind, id) =>
  state.ui.chartSeries.findIndex((e) => e && e.kind === kind && e.id === id)

/** Turns one line on or off, keeping whichever slot it already had. */
export function toggleChartSeries(kind, id) {
  const slots = state.ui.chartSeries
  const at = chartSlotOf(kind, id)
  if (at > -1) {
    slots[at] = null
    // The charts are never left with nothing on them.
    if (!slots.some(Boolean)) state.ui.chartAll = true
    return true
  }
  const free = slots.indexOf(null)
  if (free === -1) return false
  slots[free] = { kind, id }
  return true
}

export const chartSeriesFull = computed(
  () => state.ui.chartSeries.filter(Boolean).length >= MAX_CHART_SERIES,
)

/** Whether "All" can be switched off — only ever with a line to fall back on. */
export const canHideAll = computed(() => state.ui.chartSeries.some(Boolean))

/** Turns the everything series on or off: with it off, the charts are the tags. */
export function toggleChartAll() {
  if (!state.ui.chartAll) {
    state.ui.chartAll = true
    return true
  }
  if (!canHideAll.value) return false
  state.ui.chartAll = false
  return true
}


/**
 * The month after the one being viewed, and what is already committed to it.
 * Occurrence by occurrence, so every cadence is counted as it will really land.
 */
/**
 * The planned spending of the month being viewed, split by whether it has
 * happened yet. Overdue bills count as still to pay — they have not been paid
 * just because their day went by.
 */
export const plannedSpending = computed(() => {
  const rows = schedule.value.filter((r) => r.type === 'expense')
  const waiting = rows.filter((r) => !r.matches.length)
  const done = rows.filter((r) => r.matches.length)
  return {
    planned: rows.length,
    due: round2(waiting.reduce((sum, r) => sum + r.expected, 0)),
    dueCount: waiting.length,
    paid: round2(done.reduce((sum, r) => sum + r.actual, 0)),
    paidCount: done.length,
  }
})

/** How much of the month's planned spending has actually been settled. */
export const plannedProgress = computed(() => {
  const { paid, due } = plannedSpending.value
  const whole = paid + due
  return whole > 0 ? Math.min(1, paid / whole) : 0
})

/** Everything being saved towards, as one figure and one share. */
export const savingsTotals = computed(() => {
  const saved = round2(goalStates.value.reduce((sum, g) => sum + g.saved, 0))
  const target = round2(goalStates.value.reduce((sum, g) => sum + g.target, 0))
  const thisMonth = round2(goalStates.value.reduce((sum, g) => sum + g.thisMonth, 0))
  return { saved, target, thisMonth, progress: target > 0 ? Math.min(1, saved / target) : 0 }
})

export const nextMonth = computed(() => shiftMonth(viewedMonth.value, 1))
/**
 * The plans with each automatic one at the figure it expects next. Anything
 * reading `plan.amount` outside the schedule wants this rather than
 * `state.plans`, where an automatic plan's amount is only its old fixed one.
 */
export const plansAtCurrentAmounts = computed(() =>
  withAutoAmounts(state.plans, planRows.value, todayISO()),
)

export const nextMonthPlan = computed(() =>
  plannedBetween(plansAtCurrentAmounts.value, startOfMonth(nextMonth.value), endOfMonth(nextMonth.value)),
)

export const planById = computed(() => new Map(state.plans.map((p) => [p.id, p])))

/** The categories something is planned for — what a row wears its link icon for. */
export const plannedCategories = computed(
  () => new Set(state.plans.filter((p) => !p.archived && p.category).map((p) => p.category)),
)
export const goalById = computed(() => new Map(state.goals.map((g) => [g.id, g])))
export const tagById = computed(() => new Map(state.tags.map((t) => [t.id, t])))
export const categoryByName = computed(
  () => new Map(state.categories.map((c) => [c.name.toLowerCase(), c])),
)

/**
 * Every tag a transaction wears: the ones put on it, and the ones that come
 * with the category it is filed under. Inherited tags are not written anywhere
 * — the category is the link — so re-filing a row changes them with it.
 */
export const tagIdsOn = (tx) => {
  const own = tx?.ownTags ?? []
  const category = tx.category ? categoryByName.value.get(tx.category.toLowerCase()) : null
  const inherited = category ? (state.categoryTags[category.id] ?? []) : []
  return inherited.length ? [...new Set([...own, ...inherited])] : own
}

/** The tags on one transaction (a row as read, with its `ownTags`), its category's included. */
export const tagsOf = (tx) => (tx ? tagIdsOn(tx).map((id) => tagById.value.get(id)).filter(Boolean) : [])

/** Which of a transaction's tags it wears because of its category. */
export const inheritedTagsOf = (tx) => {
  const category = tx?.category ? categoryByName.value.get(tx.category.toLowerCase()) : null
  const ids = category ? (state.categoryTags[category.id] ?? []) : []
  return ids.map((id) => tagById.value.get(id)).filter(Boolean)
}

/** How many transactions wear each tag, inherited ones included. */
const tagCounts = fromDb(
  () => ({}),
  async () => {
    const rows = await ask('[]', 'SELECT tag, count(*) AS n FROM worn GROUP BY tag')
    return new Map(rows.map((r) => [r.tag, r.n]))
  },
  new Map(),
)

export const tagUsage = computed(() => new Map(state.tags.map((t) => [t.id, tagCounts.value.get(t.id) ?? 0])))

/* ------------------------------------------------------------------ */
/* The filter bar. Every filter is a list of values: any of them shows  */
/* a row, and the lists narrow each other. Empty means "not filtering". */
/* ------------------------------------------------------------------ */

/**
 * Money went out, came in, or was put by. The third is not a transaction at
 * all — it is a goal's instalment, written into the ledger so the reserving is
 * visible — but it reads as a kind, so it is filtered like one.
 */
export const kindLabel = (id) => t(`kindLabel.${id}`)

/** The two a transaction can be: the bank has finished with it, or not yet. */
export const STATUSES = ['pending', 'settled']
/** Whether files are attached to a row (docs/attachments-plan.md). */
export const FILE_STATES = ['attached', 'unattached']

/** A value every list can hold, meaning "none of them". */
export const NONE = 'none'
export const NO_TAG = NONE // the name the tag filter went by first

const NOTHING_WORDS = ['nothing', 'none', 'no', 'unplanned', 'untagged']
const KIND_WORDS = {
  income: 'income',
  received: 'income',
  in: 'income',
  spending: 'expense',
  spent: 'expense',
  expense: 'expense',
  out: 'expense',
  saving: 'savings',
  savings: 'savings',
  saved: 'savings',
}

/**
 * Every list, and everything each one needs to be shown and searched — and
 * `sql(value)`, the condition on `ledger l` (lib/ledger-sql.js) that one value
 * of it puts to the database.
 */
export const FILTERS = [
  {
    key: 'kinds',
    token: 'is',
    get label() {
      return t('filter.kind')
    },
    get title() {
      return t('filter.kind')
    },
    nothing: null, // every row is one of the three
    options: () => ['expense', 'income', 'savings'].map((id) => ({ id, name: kindLabel(id) })),
    // Savings are their own kind rather than expenses that happen to be
    // negative: asking for spending should not sweep the projections in.
    sql: (id) =>
      clause(
        id === 'savings'
          ? 'l.saving = 1'
          : id === 'income'
            ? '(l.saving = 0 AND l.amount > 0)'
            : '(l.saving = 0 AND l.amount < 0)',
      ),
  },
  // Whether the bank has finished with a row (`transactions.settled`). Only
  // real money has a status: the goals' savings are neither.
  {
    key: 'status',
    token: 'status',
    get label() {
      return t('filter.status')
    },
    get title() {
      return t('filter.status')
    },
    nothing: null, // every transaction is one or the other
    options: () => STATUSES.map((id) => ({ id, name: t(`filter.${id}`) })),
    sql: (id) => clause(`(l.saving = 0 AND l.settled = ${id === 'pending' ? 0 : 1})`),
  },
  // Whether files are attached to it. Only real money has any.
  {
    key: 'files',
    token: 'files',
    get label() {
      return t('filter.files')
    },
    get title() {
      return t('filter.files')
    },
    nothing: null, // every transaction has files or has none
    options: () => FILE_STATES.map((id) => ({ id, name: t(`filter.${id}`) })),
    sql: (id) =>
      clause(
        id === 'attached'
          ? '(l.saving = 0 AND EXISTS (SELECT 1 FROM attachments a WHERE a.transaction_id = l.id))'
          : '(l.saving = 0 AND NOT EXISTS (SELECT 1 FROM attachments a WHERE a.transaction_id = l.id))',
      ),
  },
  {
    key: 'tags',
    token: 'tag',
    get label() {
      return t('filter.tag')
    },
    get title() {
      return t('filter.tag')
    },
    get nothing() {
      return t('filter.nothing')
    },
    options: () => state.tags.map((tag) => ({ id: tag.id, name: tag.name })),
    // A saving wears no tags, so a tag filter is never about one — not even
    // "none", which is a question about how a transaction was filed.
    sql: (id) =>
      id === NONE
        ? clause('(l.saving = 0 AND NOT EXISTS (SELECT 1 FROM worn w WHERE w.tx = l.id))')
        : clause('(l.saving = 0 AND EXISTS (SELECT 1 FROM worn w WHERE w.tx = l.id AND w.tag = ?))', [id]),
  },
  {
    key: 'categories',
    token: 'category',
    get label() {
      return t('filter.category')
    },
    get title() {
      return t('filter.category')
    },
    get nothing() {
      return t('filter.nothing')
    },
    // A category is the transaction's own text, so the id is the name. Goals'
    // virtual categories are here too, which is how one filters to a goal.
    options: () => viewableCategories.value,
    sql: (id) => (id === NONE ? clause("l.category = ''") : clause('l.category = ?', [id])),
  },
  // The two bounds are filters like the rest, but they hold one date each
  // rather than a list — so they are set, not toggled, and the pill is a date
  // picker rather than a label.
  {
    key: 'from',
    token: 'from',
    get label() {
      return t('filter.dateFrom')
    },
    get title() {
      return t('filter.dateFrom')
    },
    date: true,
    sql: (value) => clause('l.date >= ?', [value]),
  },
  {
    key: 'to',
    token: 'to',
    get label() {
      return t('filter.dateTo')
    },
    get title() {
      return t('filter.dateTo')
    },
    date: true,
    sql: (value) => clause('l.date <= ?', [value]),
  },
  // Sizes, not signs: `min:50` is "fifty or more, whichever way it went", the
  // same reading the amount sort uses. Which way is what the kind filter says.
  {
    key: 'amountFrom',
    token: 'min',
    get label() {
      return t('filter.amountFrom')
    },
    get title() {
      return t('filter.amountFrom')
    },
    amount: true,
    sql: (value) => clause('abs(l.amount) >= ?', [value]),
  },
  {
    key: 'amountTo',
    token: 'max',
    get label() {
      return t('filter.amountTo')
    },
    get title() {
      return t('filter.amountTo')
    },
    amount: true,
    sql: (value) => clause('abs(l.amount) <= ?', [value]),
  },
]

/** A filter that holds one value rather than a list. */
const isSingle = (filter) => filter.date || filter.amount

/** The two that say *when* to look rather than *what* to count. */
const RANGE_KEYS = ['from', 'to']

const filterByKey = (key) => FILTERS.find((f) => f.key === key)

/** The name a value goes by, for a pill or a suggestion row. */
function valueName(filter, id) {
  if (id === NONE) return filter.nothing ?? t('filter.nothing')
  return filter.options().find((o) => o.id === id)?.name ?? id
}

/**
 * The view as one condition on `ledger l`: each list any of its values, the
 * lists and the bounds together, and every word of the search somewhere on the
 * row. Plain data (`{ sql, params }`), so a question can carry it.
 */
export const viewCondition = computed(() => {
  const parts = FILTERS.map((filter) => {
    const chosen = state.ui[filter.key]
    if (isSingle(filter)) return chosen == null ? null : filter.sql(chosen)
    return chosen.length ? or(chosen.map((id) => filter.sql(id))) : null
  })
  const terms = state.ui.search.trim().toLowerCase().split(/\s+/).filter(Boolean)
  for (const term of terms) parts.push(clause(`${HAYSTACK} LIKE ? ESCAPE '\\'`, [containing(term)]))
  return and(parts)
})

export const anyFilter = computed(() =>
  FILTERS.some((f) => (isSingle(f) ? state.ui[f.key] != null : state.ui[f.key].length > 0)),
)

/** One pill per list that has something in it, its values joined by "or". */
export const filterPills = computed(() =>
  FILTERS.filter((filter) =>
    isSingle(filter) ? state.ui[filter.key] != null : state.ui[filter.key].length,
  )
    .map((filter) =>
      filter.date
        ? {
            key: filter.key,
            label: filter.label,
            value: fullDay(state.ui[filter.key]),
            date: state.ui[filter.key],
          }
        : filter.amount
          ? {
              key: filter.key,
              label: filter.label,
              value: String(state.ui[filter.key]),
              amount: state.ui[filter.key],
            }
          : {
            key: filter.key,
            label: filter.label,
              value: state.ui[filter.key]
                .map((id) => valueName(filter, id))
                .join(` ${t('filter.or')} `),
            },
    ),
)

export function clearFilter(key) {
  const filter = filterByKey(key)
  if (filter) state.ui[key] = isSingle(filter) ? null : []
}

export function clearFilters() {
  for (const filter of FILTERS) state.ui[filter.key] = isSingle(filter) ? null : []
  state.ui.search = ''
}

/** Moves one of the date bounds; null takes it off. */
export function setDateFilter(key, date) {
  if (filterByKey(key)?.date) state.ui[key] = date || null
}

/** Moves one of the amount bounds; null takes it off. */
export function setAmountFilter(key, amount) {
  if (!filterByKey(key)?.amount) return
  const value = typeof amount === 'number' ? amount : parseAmount(amount)
  state.ui[key] = Number.isFinite(value) ? Math.abs(round2(value)) : null
}

/** Turns one value of one list on or off. */
export function toggleFilter(key, id) {
  const filter = filterByKey(key)
  // Counted when a value is put on, not taken off.
  const on = filter?.date || filter?.amount ? (id ?? null) !== null : !state.ui[key]?.includes(id)
  if (filter && on) count('filter', key)
  if (filter?.date) return setDateFilter(key, id)
  if (filter?.amount) return setAmountFilter(key, id)
  const chosen = state.ui[key]
  if (!chosen) return
  const at = chosen.indexOf(id)
  if (at > -1) chosen.splice(at, 1)
  else chosen.push(id)
}

/** The tag filter, by the name the rest of the app already calls it. */
export const toggleTagFilter = (id) => toggleFilter('tags', id)

/** Replaces a list outright — what clicking through from a plan or an alert does. */
export function setFilter(key, ids) {
  const filter = filterByKey(key)
  if (!filter) return
  state.ui[key] = isSingle(filter) ? (ids ?? null) : [...ids]
}

/** `2026-09-01`, `1.9.2026`, `today`, `yesterday`. */
function readDate(value) {
  if ('today'.startsWith(value)) return todayISO()
  if ('yesterday'.startsWith(value)) return addDays(todayISO(), -1)
  return parseDateCell(value)
}

const matchName = (options, value) =>
  options.find((o) => o.name.toLowerCase() === value) ??
  options.find((o) => o.name.toLowerCase().startsWith(value)) ??
  options.find((o) => o.name.toLowerCase().includes(value))

/**
 * Reads `tag:food`, `is:income`, `plan:rent`, `goal:house` out of the search
 * box — the typed way to the same filters the list offers.
 *
 * @returns {{label: string, patch: {key: string, id: string}}|null}
 */
export function readFilterToken(text) {
  const match = String(text ?? '').trim().match(/^([a-z]+)\s*:\s*(.+)$/i)
  if (!match) return null
  const token = match[1].toLowerCase()
  const value = match[2].trim().toLowerCase()
  const filter = FILTERS.find((f) => f.token === token)
  if (!filter) return null

  if (filter.date) {
    const date = readDate(value)
    return date ? { label: `${filter.label}: ${fullDay(date)}`, patch: { key: filter.key, id: date } } : null
  }
  if (filter.amount) {
    const amount = parseAmount(value)
    if (!Number.isFinite(amount)) return null
    const size = Math.abs(round2(amount))
    return { label: `${filter.label}: ${size}`, patch: { key: filter.key, id: size } }
  }

  const nothing = NOTHING_WORDS.some((word) => word.startsWith(value))
  if (filter.key === 'kinds') {
    const kind = KIND_WORDS[value]
    if (kind) {
      return {
        label: `${t('filter.kind')}: ${kindLabel(kind)}`,
        patch: { key: 'kinds', id: kind },
      }
    }
    // `is:pending` reads as naturally as `status:pending`: it is a question
    // about what a row is, and the status filter answers it.
    const status = STATUSES.find((id) => id.startsWith(value))
    if (status) {
      return {
        label: `${t('filter.status')}: ${t(`filter.${status}`)}`,
        patch: { key: 'status', id: status },
      }
    }
    // `is:unplanned` predates the category filter; it means the uncategorised
    // ones now, which is the same question asked of the model there is.
    if ('unplanned'.startsWith(value)) {
      return {
        label: `${t('filter.category')}: ${t('filter.nothing')}`,
        patch: { key: 'categories', id: NONE },
      }
    }
    return null
  }
  const options = filter.options()
  if (nothing && filter.nothing && options.length) {
    return { label: `${filter.label}: ${filter.nothing}`, patch: { key: filter.key, id: NONE } }
  }
  const hit = matchName(options, value)
  return hit ? { label: `${filter.label}: ${hit.name}`, patch: { key: filter.key, id: hit.id } } : null
}

export function applyFilterPatch(patch) {
  if (patch?.key) toggleFilter(patch.key, patch.id)
}

/** Turns a read token into a pill, clearing the text it was typed as. */
export function applyFilterToken(token) {
  if (!token) return false
  applyFilterPatch(token.patch)
  state.ui.search = ''
  return true
}

/**
 * Everything you could filter by, narrowed to what has been typed — the list
 * the search box shows the moment it is focused, so nobody has to already know
 * that `tag:` is a thing.
 */
export function suggestFilters(query = '') {
  const text = String(query).trim().toLowerCase()
  const [, typedToken, typedValue] = text.match(/^([a-z]+)\s*:\s*(.*)$/i) ?? []
  const value = (typedValue ?? text).trim()

  return FILTERS.filter((filter) => !typedToken || filter.token === typedToken.toLowerCase())
    .map((filter) => {
      if (filter.amount) {
        const typed = value ? parseAmount(value) : NaN
        const matches = !value || Number.isFinite(typed) || filter.title.toLowerCase().includes(value)
        // Starting values that show everything: the reader narrows from there.
        const start = Number.isFinite(typed)
          ? Math.abs(round2(typed))
          : filter.key === 'amountFrom'
            ? 0
            : round2(listTotals.value.biggest)
        return {
          key: filter.token,
          title: filter.title,
          rows: matches
            ? [
                {
                  id: start,
                  name: Number.isFinite(typed) ? `${filter.title}: ${start}` : filter.title,
                  patch: { key: filter.key, id: start },
                  on: state.ui[filter.key] != null,
                },
              ]
            : [],
        }
      }
      if (filter.date) {
        // One row, and picking it starts from today — a bound you then move.
        const typedDate = value ? readDate(value) : null
        const matches = !value || typedDate || filter.title.toLowerCase().includes(value)
        return {
          key: filter.token,
          title: filter.title,
          rows: matches
            ? [
                {
                  id: typedDate ?? todayISO(),
                  name: typedDate ? fullDay(typedDate) : filter.title,
                  patch: { key: filter.key, id: typedDate ?? todayISO() },
                  on: !!state.ui[filter.key],
                },
              ]
            : [],
        }
      }
      const options = filter.options()
      const rows = options.length && filter.nothing
        ? [{ id: NONE, name: filter.nothing }, ...options]
        : options
      const groupMatches = !typedToken && !!value && filter.token.startsWith(value)
      return {
        key: filter.token,
        title: filter.title,
        rows: rows
          .filter((row) => !value || groupMatches || row.name.toLowerCase().includes(value))
          .map((row) => ({
            ...row,
            patch: { key: filter.key, id: row.id },
            on: state.ui[filter.key].includes(row.id),
          })),
      }
    })
    .filter((group) => group.rows.length)
}

/**
 * The category names, commonest first. The table is the list, and anything in
 * use but not in it yet — an import, a row the SQL console wrote — is counted
 * too rather than being invisible until someone tidies up.
 */
const categoryCounts = fromDb(
  () => ({}),
  async () => {
    const rows = await query('SELECT category_id AS id, count(*) AS n FROM transactions WHERE category_id IS NOT NULL GROUP BY category_id')
    return new Map(rows.map((r) => [r.id, r.n]))
  },
  new Map(),
)

/** How many transactions are filed under each category, by name. */
export const categoryUsage = computed(
  () => new Map(state.categories.map((c) => [c.name, categoryCounts.value.get(c.id) ?? 0])),
)

export const categories = computed(() => {
  const counts = categoryCounts.value
  return state.categories
    .map((c, order) => ({ name: c.name, n: counts.get(c.id) ?? 0, order }))
    .sort((a, b) => b.n - a.n || a.order - b.order)
    .map((c) => c.name)
})

/** The same list in the shape a picker wants: a category is its own name. */
export const categoryOptions = computed(() =>
  categories.value.map((name) => ({ id: name, name })),
)

/**
 * Every live goal as a *virtual* category, named after it: what its savings
 * rows are filed under. They are offered wherever a category is *looked at* —
 * the category filter, the chart chips — and nowhere one is *chosen for a
 * transaction*, because nothing real can be put aside by filing it. A goal
 * that shares a name with a real category is that category's line already.
 */
export const goalCategories = computed(() => {
  const real = new Set(categories.value.map((name) => name.toLowerCase()))
  return state.goals
    .filter((goal) => !goal.archived && goal.name && !real.has(goal.name.toLowerCase()))
    .map((goal) => ({ id: goal.name, name: goal.name, goal: true }))
})

/** What a category can be looked at by: the real ones, then the goals'. */
export const viewableCategories = computed(() => [...categoryOptions.value, ...goalCategories.value])

/**
 * The same list with one more on it — a category being typed into a form that
 * nothing has used yet. Without it a brand-new name would be chosen but have
 * nothing to show for it, since a picker draws its chips from the options.
 */
export const withCategory = (name) => {
  const wanted = String(name ?? '').trim()
  const known =
    !wanted || categories.value.some((c) => c.toLowerCase() === wanted.toLowerCase())
  return known ? categoryOptions.value : [{ id: wanted, name: wanted }, ...categoryOptions.value]
}

/**
 * Every payment type the ledger already uses, most common first, in the
 * picker's `{ id, name }` shape. There is no table behind it: a payment type is
 * only text on a row, so the list is whatever the rows say.
 */
export const paymentTypeOptions = fromDb(
  () => ({}),
  async () => {
    const rows = await query(
      `SELECT trim(payment_type) AS name, count(*) AS n FROM transactions
        WHERE trim(payment_type) <> '' GROUP BY 1 ORDER BY n DESC, name`,
    )
    return rows.map((r) => ({ id: r.name, name: r.name }))
  },
  [],
)

/** As `withCategory`: a payment type being typed in is among the options. */
export const withPaymentType = (name) => {
  const wanted = String(name ?? '').trim()
  const known =
    !wanted || paymentTypeOptions.value.some((o) => o.name.toLowerCase() === wanted.toLowerCase())
  return known ? paymentTypeOptions.value : [{ id: wanted, name: wanted }, ...paymentTypeOptions.value]
}

/**
 * What the view holds, added up: how many rows are listed (savings included)
 * and how many of them are real, the money in and out among the real ones,
 * the net of everything shown, and the largest size — where an amount filter
 * starts from.
 */
export const listTotals = fromDb(
  () => ({ savings: savingsJson.value, condition: viewCondition.value }),
  async ({ savings, condition }) => {
    const w = where(condition)
    const [r] = await ask(
      savings,
      `SELECT count(*) AS shown,
              total(l.saving = 0) AS real,
              total(CASE WHEN l.saving = 0 AND l.amount > 0 THEN l.amount END) AS income,
              total(CASE WHEN l.saving = 0 AND l.amount < 0 THEN -l.amount END) AS spent,
              total(l.amount) AS net,
              max(CASE WHEN l.saving = 0 THEN abs(l.amount) END) AS biggest
         FROM ledger l ${w.sql}`,
      w.params,
    )
    return { shown: r.shown, real: r.real, income: r.income, spent: r.spent, net: r.net, biggest: r.biggest ?? 0 }
  },
  { shown: 0, real: 0, income: 0, spent: 0, net: 0, biggest: 0 },
)

/** The real money the list shows, as its header says it. */
export const visibleTotals = computed(() => {
  const { income, spent, real } = listTotals.value
  return { income, spent, net: income - spent, count: real }
})

/**
 * Every real transaction in the view, in the list's order, for an export: the
 * columns a person reads, with the tags each wears (its category's included)
 * as names. The savings are left out — they are no one's transactions.
 */
export async function rowsForExport() {
  const w = where(and([clause('l.saving = 0'), viewCondition.value]))
  return ask(
    savingsJson.value,
    `SELECT l.date, l.description, l.amount, l.category, l.payment_type, l.notes, l.settled,
            COALESCE((SELECT group_concat(g.name, ', ') FROM worn w JOIN tags g ON g.id = w.tag WHERE w.tx = l.id), '') AS tags
       FROM ledger l ${w.sql}
      ORDER BY ${listOrder.value}`,
    w.params,
  )
}

/** The ids of every real transaction in the view, loaded or not — for "all". */
export async function idsInView() {
  const w = where(and([clause('l.saving = 0'), viewCondition.value]))
  const rows = await ask(savingsJson.value, `SELECT l.id FROM ledger l ${w.sql}`, w.params)
  return rows.map((r) => r.id)
}

/* ------------------------------------------------------------------ */
/* Sorting. Date keeps the day headings; the others are a flat list,     */
/* since a day heading over rows from six different days is a lie.       */
/* ------------------------------------------------------------------ */

export const SORTS = [
  { key: 'date', label: 'Date' },
  { key: 'description', label: 'Description' },
  { key: 'amount', label: 'Amount' },
]

export function setSort(by) {
  const sort = state.ui.sort
  if (sort.by === by) sort.dir = sort.dir === 'asc' ? 'desc' : 'asc'
  else Object.assign(sort, { by, dir: by === 'description' ? 'asc' : 'desc' })
}

/* ------------------------------------------------------------------ */
/* The list, a page at a time. The database filters, searches and sorts; */
/* the rows arrive a hundred at a time as the list is scrolled.          */
/* ------------------------------------------------------------------ */

export const PAGE_SIZE = 100

/**
 * The order the list is asked for. Ties go to the day, then to when a row was
 * written — a goal's saving is written at the day's end, so it sits last — and
 * finally the id, so a page boundary never falls between two equal rows in an
 * order that could differ the next time it is asked.
 */
const listOrder = computed(() => {
  const dir = state.ui.sort.dir === 'asc' ? 'ASC' : 'DESC'
  const byDate = `l.date ${dir}, l.created_at ${dir}, l.id ${dir}`
  // By size, not by sign: "the big ones" is one question, and whether the
  // money came or went is what the kind filter is for.
  if (state.ui.sort.by === 'amount') return `abs(l.amount) ${dir}, ${byDate}`
  if (state.ui.sort.by === 'description') return `fold(l.description) ${dir}, ${byDate}`
  return byDate
})

/**
 * The goals' savings are rows of the ledger like any other — `LEDGER` holds
 * them — so the list, its filters and its sorting take them in with the real
 * money. They are display only: nothing counts them as income or spending,
 * because the balance has already taken them off through `setAsideOn`.
 */
const listQuestion = computed(() => ({
  savings: savingsJson.value,
  condition: viewCondition.value,
  order: listOrder.value,
}))

async function readPage(question, offset, limit) {
  const w = where(question.condition)
  const rows = await ask(
    question.savings,
    `SELECT ${ROW_COLUMNS} FROM ledger l ${w.sql} ORDER BY ${question.order} LIMIT ? OFFSET ?`,
    [...w.params, limit, offset],
  )
  return rows.map(readRow)
}

let listTicket = 0
let listKey = null

/**
 * Reads the list from the top. A new view starts over at one page; the same
 * view asked again — something was written — keeps as many rows as it had, so
 * an edit deep down the list does not throw the reader back to the top.
 */
async function reloadList(keep) {
  const mine = ++listTicket
  const question = listQuestion.value
  const limit = Math.max(PAGE_SIZE, keep)
  state.ledger.loading = true
  try {
    const rows = await tracked(readPage(question, 0, limit))
    if (mine !== listTicket) return
    state.ledger.rows = rows
    state.ledger.done = rows.length < limit
  } catch (err) {
    if (err?.code !== 'disconnected') console.error('OKANE could not read the list', err)
  } finally {
    if (mine === listTicket) state.ledger.loading = false
  }
}

/** The next page, onto the end — what scrolling to the bottom asks for. */
export async function loadMore() {
  const ledger = state.ledger
  if (!state.ready || ledger.loading || ledger.done) return
  const mine = listTicket
  ledger.loading = true
  try {
    const rows = await tracked(readPage(listQuestion.value, ledger.rows.length, PAGE_SIZE))
    // The view changed while this was on its way: the rows are for another list.
    if (mine !== listTicket) return
    ledger.rows = [...ledger.rows, ...rows]
    ledger.done = rows.length < PAGE_SIZE
  } catch (err) {
    if (err?.code !== 'disconnected') console.error('OKANE could not read more of the list', err)
  } finally {
    if (mine === listTicket) ledger.loading = false
  }
}

watch(
  () => (state.ready ? [state.dataVersion, JSON.stringify(listQuestion.value)] : null),
  (next) => {
    if (!next) return
    const sameView = next[1] === listKey
    listKey = next[1]
    reloadList(sameView ? state.ledger.rows.length : 0)
  },
  { immediate: true },
)

/** The rows read so far, in the order asked for, savings among them. */
export const sortedTransactions = computed(() => state.ledger.rows)

/**
 * What each day came to — all of it, not only the rows read so far: a day
 * whose rows straddle two pages is still one day. Asked for the days the list
 * has reached, while it is sorted by date.
 */
const dayTotals = fromDb(
  () => {
    const rows = state.ledger.rows
    if (state.ui.sort.by !== 'date' || !rows.length) return null
    const dates = [rows[0].date, rows[rows.length - 1].date].sort()
    return { savings: savingsJson.value, condition: viewCondition.value, from: dates[0], to: dates[1] }
  },
  async ({ savings, condition, from, to }) => {
    const w = where(and([condition, clause('l.date BETWEEN ? AND ?', [from, to])]))
    const rows = await ask(savings, `SELECT l.date, total(l.amount) AS total FROM ledger l ${w.sql} GROUP BY l.date`, w.params)
    return new Map(rows.map((r) => [r.date, r.total]))
  },
  new Map(),
)

/** The list's sections: one per day while sorting by date, otherwise just one. */
export const transactionDays = computed(() => {
  const rows = sortedTransactions.value
  if (state.ui.sort.by !== 'date') {
    return rows.length ? [{ date: null, items: rows, total: listTotals.value.net }] : []
  }
  const days = []
  for (const tx of rows) {
    const last = days[days.length - 1]
    if (last && last.date === tx.date) last.items.push(tx)
    else days.push({ date: tx.date, items: [tx], total: 0 })
  }
  // Savings count here: a day's total is what the balance did that day, and the
  // balance does step down when a goal takes its instalment.
  for (const day of days) {
    day.total = dayTotals.value.get(day.date) ?? day.items.reduce((sum, t) => sum + t.amount, 0)
  }
  return days
})

export const planHealth = computed(() => {
  const rows = schedule.value
  return {
    total: rows.length,
    matched: rows.filter((r) => r.matches.length).length,
    missing: rows.filter((r) => r.status === 'missing').length,
    off: rows.filter((r) => r.status === 'over' || r.status === 'under').length,
  }
})
